import { useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { NaceDetailArray, AffinityDto } from 'models/dtoTypes';
import DoNoSignificantHarmNaceDetailBox from './_doNoSignificantHarmNaceDetailBox';
import SubSection from './_subSection';
import Empty from './_empty';
import { dnshSvg } from 'kosmos/images';
import { useTranslation } from 'react-i18next';

const PREFIX = 'DoNoSignificantHarmDetailReport';

const classes = {
  item: `${PREFIX}-item`,
};

const Root = styled('div')(() => ({
  [`& .${classes.item}`]: {
    width: '100%',
  },
}));

interface IProps {
  name?: string;
  doNoSignificantHarmDetailReport: NaceDetailArray[];
}

export default function DoNoSignificantHarmDetailReport(props: IProps): JSX.Element {
  const { t } = useTranslation('default');
  const {
    name = t('eu-taxonomy-regulation-do-no-significant-harm-dnsh-score-in-detail'),
    doNoSignificantHarmDetailReport,
  } = props;

  const filterByAffinity = (list: NaceDetailArray[], affinity: AffinityDto): NaceDetailArray[] => {
    if (list && list.length > 0) {
      return list.filter((item) => item.overallAffinity === affinity && item.affinityDescriptor);
    } else {
      return [];
    }
  };

  const positiveList = useMemo<NaceDetailArray[]>(() => {
    return filterByAffinity(doNoSignificantHarmDetailReport, 'positive');
  }, doNoSignificantHarmDetailReport);

  const negativeList = useMemo<NaceDetailArray[]>(() => {
    return filterByAffinity(doNoSignificantHarmDetailReport, 'negative');
  }, doNoSignificantHarmDetailReport);

  const warningList = useMemo<NaceDetailArray[]>(() => {
    return filterByAffinity(doNoSignificantHarmDetailReport, 'warning');
  }, doNoSignificantHarmDetailReport);

  const neutralList = useMemo<NaceDetailArray[]>(() => {
    return filterByAffinity(doNoSignificantHarmDetailReport, 'neutral');
  }, doNoSignificantHarmDetailReport);

  const renderSection = (title: string, list: NaceDetailArray[]) => {
    if (list.length === 0) {
      return <></>;
    } else {
      return (
        <>
          <h3>{title}</h3>
          {list.map((item, index) => (
            <div key={index} className={classes.item}>
              <DoNoSignificantHarmNaceDetailBox key={index} naceDetailArray={item} />
            </div>
          ))}
        </>
      );
    }
  };

  const renderContent = () => {
    if (
      positiveList.length === 0 &&
      negativeList.length === 0 &&
      warningList.length === 0 &&
      neutralList.length === 0
    ) {
      return <Empty title={t('there-is-no-data-available')} />;
    }

    return (
      <Root>
        {renderSection(t('positive-contributions'), positiveList)}
        {renderSection(t('negative-contributions'), negativeList)}
        {renderSection(t('ambiguous-contributions'), warningList)}
        {renderSection(t('neutral-contributions'), neutralList)}
      </Root>
    );
  };

  return (
    <SubSection title={name} icon={<img src={dnshSvg} />}>
      {renderContent()}
    </SubSection>
  );
}
